import type { DateSelectArg, EventApi } from '@fullcalendar/core';
import type { Absence, ResourceUser } from '@src/features/absence';
import { formatIsoDate, isNextDay, subtractDay, toTime } from '@src/shared/lib';
import type { SelectedAbsence, TimelineSlot, VisibleRange } from '../types';
import {
  formatDurationLabel,
  getAbsencesIdsForRange,
  getEventLabel,
  getUserAbsences,
} from './absence';
import { ABSENCE_BLOCK_POSITION_SINGLE } from './constants';
import { getUserWorktimeForDate } from './resources';

const FIXED_MONTH_DAY_COUNT = 31;

export function getFixedMonthVisibleRange(anchorDate: Date): VisibleRange {
  const start = new Date(
    anchorDate.getFullYear(),
    anchorDate.getMonth(),
    anchorDate.getDate(),
  );
  const end = new Date(start);
  end.setDate(end.getDate() + FIXED_MONTH_DAY_COUNT);

  return { start, end };
}

export function getInitialVisibleRange(): VisibleRange {
  const now = new Date();
  return getFixedMonthVisibleRange(
    new Date(now.getFullYear(), now.getMonth(), 1),
  );
}

/**
 * Collects absence details for a given absence ID.
 *
 * Retrieves key attributes from the targeted absence record and, if available,
 * from its associated neighbors (whole activity).
 *
 * @param {number} absenceId
 *        Unique identifier of the absence to collect.
 * @param {Array<Absence>} absences
 *        List of absence objects to search through.
 * @param {boolean} single
 *        Gets data for single absenece object with specified id or
 *        for all absences if it is multiday absence event
 */
export function getAbsenceSelectionById(
  absenceId: number,
  absences: Absence[],
  single = false,
): SelectedAbsence | null {
  if (!absenceId || !Array.isArray(absences) || absences.length === 0) {
    return null;
  }

  const anchor = absences.find((a) => a.id === absenceId);
  if (!anchor) {
    return null;
  }

  if (
    single ||
    !anchor.allDay ||
    anchor.blockPosition === ABSENCE_BLOCK_POSITION_SINGLE
  ) {
    return hydrateSelectedAbsence(
      {
        userId: anchor.userId,
        start: formatIsoDate(anchor.date),
        end: formatIsoDate(anchor.date),
        startTime: anchor.startTime,
        endTime: anchor.endTime,
        absenceIds: [anchor.id],
      },
      absences,
    );
  }

  const block = getAbsenceBlock(anchor, absences);
  if (!block.length) {
    return null;
  }

  return hydrateSelectedAbsence(
    {
      userId: block[0].userId,
      start: formatIsoDate(block[0].date),
      end: formatIsoDate(block[block.length - 1].date),
      startTime: null,
      endTime: null,
      absenceIds: block.map((a) => a.id),
    },
    absences,
  );
}

function getAbsenceBlock(anchor: Absence, absences: Absence[]): Absence[] {
  const sameGroup = (a: Absence) => {
    return (
      anchor.typeId != null &&
      a.userId === anchor.userId &&
      a.allDay &&
      a.typeId === anchor.typeId &&
      a.absenceTentative === anchor.absenceTentative &&
      a.approvedManager === anchor.approvedManager &&
      a.approvedHr === anchor.approvedHr
    );
  };

  const candidates = absences
    .filter(sameGroup)
    .sort((a, b) => a.date.getTime() - b.date.getTime());

  const anchorIdx = candidates.findIndex((a) => a.id === anchor.id);
  if (anchorIdx === -1) {
    return [];
  }

  let left = anchorIdx;
  while (
    left - 1 >= 0 &&
    isNextDay(candidates[left - 1].date, candidates[left].date)
  ) {
    left -= 1;
  }

  let right = anchorIdx;
  while (
    right + 1 < candidates.length &&
    isNextDay(candidates[right].date, candidates[right + 1].date)
  ) {
    right += 1;
  }

  return candidates.slice(left, right + 1);
}

export function hydrateSelectedAbsence(
  selection: SelectedAbsence,
  absences: Absence[],
): SelectedAbsence {
  if (!selection.absenceIds.length || !absences.length) {
    return selection;
  }

  const snapshotAbsences = selection.absenceIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => !!absence);

  if (!snapshotAbsences.length) {
    return selection;
  }

  const primaryAbsenceId =
    selection.primaryAbsenceId ?? selection.absenceIds[0];
  const primaryAbsence =
    snapshotAbsences.find((absence) => absence.id === primaryAbsenceId) ??
    snapshotAbsences[0];
  const blockHead =
    primaryAbsence.allDay &&
    primaryAbsence.blockPosition !== ABSENCE_BLOCK_POSITION_SINGLE
      ? getAbsenceBlock(primaryAbsence, absences)[0] ?? primaryAbsence
      : primaryAbsence;

  return {
    ...selection,
    primaryAbsenceId: primaryAbsence.id,
    typeId: primaryAbsence.typeId ?? null,
    absenceTentative: primaryAbsence.absenceTentative ?? false,
    approvedManager: primaryAbsence.approvedManager ?? null,
    approvedHr: primaryAbsence.approvedHr ?? null,
    notes: blockHead.notes ?? primaryAbsence.notes ?? '',
    snapshotAbsences,
  };
}

export function getSlotDatefromSelection(
  info: DateSelectArg,
  absences: Absence[],
): TimelineSlot {
  const end = info.allDay ? subtractDay(info.end, 1) : info.end;
  const userId = info.resource?.extendedProps.userId;
  const userAbsences = getUserAbsences(userId, absences);
  const startTime = info.allDay ? null : toTime(info.start);
  const endTime = info.allDay ? null : toTime(info.end);
  const slot: TimelineSlot = {
    resourceId: info?.resource?.id,
    start: formatIsoDate(info.start),
    end: formatIsoDate(end),
    startTime,
    endTime,
    selectedIds: getAbsencesIdsForRange(info.start, end, userAbsences, {
      dayView: !info.allDay,
      startTime,
      endTime,
    }),
    editedAbsenceId: null,
    userId: info.resource?.extendedProps.userId,
  };
  return slot;
}

export function getSlotFromEvent(event: EventApi): TimelineSlot | null {
  if (!event.start) return null;
  if (!event.end) return null;

  const absenceId = Number(event.extendedProps.absenceId ?? event.id);
  const end = event.allDay ? subtractDay(event.start, 1) : event.end;
  return {
    resourceId: event.getResources()[0].id,
    start: formatIsoDate(event.startStr),
    end: formatIsoDate(end),
    startTime: event.allDay ? null : toTime(event.start),
    endTime: event.allDay ? null : toTime(event.end),
    selectedIds: [absenceId],
    editedAbsenceId: absenceId,
    userId: event.getResources()[0].extendedProps.userId,
  };
}

/*
   Returns an array of free dates  within the slot range that are
   not occupied by absences.
*/
export function getSlotFreeDates(
  slot: TimelineSlot,
  absences: Absence[],
): Date[] {
  if (!slot || !slot.start || !slot.end) {
    return [];
  }

  if (slot.end < slot.start) {
    return [];
  }
  const start = slot.start;
  const end = slot.end;

  const occupied = new Set(
    (absences || [])
      .filter((a) => !(a.dateStr < start) && !(a.dateStr > end))
      .map((a) => a.dateStr),
  );
  const free = [];
  let cur = new Date(slot.start);

  while (cur <= new Date(slot.end)) {
    if (!occupied.has(formatIsoDate(cur))) {
      free.push(cur);
    }
    const nextDate = cur.getDate() + 1;
    cur = new Date(cur);
    cur.setDate(nextDate);
  }
  return free;
}

export function getTransientEventsForSlot(
  slot: TimelineSlot,
  absences: Array<Absence>,
  users: Array<ResourceUser>,
  dayView = false,
) {
  if (!slot || !slot.start || !slot.end || !slot.userId) {
    return [];
  }
  const userAbsences = getUserAbsences(slot.userId, absences);
  if (dayView) {
    if (slot.editedAbsenceId) {
      return [];
    }
    const overlappingIds = getAbsencesIdsForRange(
      slot.start,
      slot.end,
      userAbsences,
      {
        dayView: true,
        startTime: slot.startTime,
        endTime: slot.endTime,
        excludeIds: slot.editedAbsenceId ? [slot.editedAbsenceId] : [],
      },
    );
    if (overlappingIds.length > 0) {
      return [];
    }

    const userWorktime = getUserWorktimeForDate(slot.userId, users, slot.start);
    const startTime = slot.startTime || userWorktime?.start;
    const endTime = slot.endTime || userWorktime?.end;
    const label =
      startTime && endTime
        ? formatDurationLabel(startTime, endTime)
        : getEventLabel(slot.start, slot.end, true);
    return [
      {
        id: `transient-event-${slot.start}`,
        resourceId: slot.resourceId,
        start: formatIsoDate(slot.start, startTime),
        end: formatIsoDate(slot.end, endTime),
        classNames: [
          'transient-event',
          'transient-edge-left',
          'transient-edge-right',
        ],
        extendedProps: { label: label },
        startEditable: false,
        durationEditable: false,
        resourceEditable: false,
      },
    ];
  }

  const slotFreeDates = getSlotFreeDates(slot, userAbsences);
  if (!slotFreeDates.length) {
    return [];
  }
  // const user = users.find((u) => u.id === slot.userId);
  // const worktime = user.worktime;

  const transientEvents = slotFreeDates.map((day, idx) => {
    const label = getEventLabel(day, day, dayView);
    const isLast = idx === slotFreeDates.length - 1;
    const event = {
      id: `transient-event-${formatIsoDate(day)}`,
      resourceId: slot.resourceId,
      start: formatIsoDate(day),
      end: formatIsoDate(day),
      classNames: [
        'transient-event',
        'transient-month-event',
        'transient-edge-left',
        isLast ? 'transient-edge-right' : 'transient-join-right',
      ],
      extendedProps: { label: label },
      editable: false,
    };

    return event;
  });
  return transientEvents;
}

export function isAbsenceSelected(
  slot: TimelineSlot,
  absences: Absence[],
): slot is TimelineSlot & { start: string; end: string; userId: number } {
  if (!slot.start || !slot.end || !slot.userId || !slot.selectedIds.length) {
    return false;
  }

  const userAbsences = getUserAbsences(slot.userId, absences);
  if (getSlotFreeDates(slot, userAbsences).length > 0) {
    return false;
  }

  const selectedIds = [...slot.selectedIds].sort((a, b) => a - b);

  const selectedAbsences = selectedIds
    .map((id) => absences.find((absence) => absence.id === id))
    .filter((absence): absence is Absence => !!absence);

  if (selectedAbsences.length !== selectedIds.length) {
    return false;
  }

  const [firstAbsence] = selectedAbsences;
  if (!firstAbsence) {
    return false;
  }

  const groupedSelection = getAbsenceSelectionById(firstAbsence.id, absences);
  const singleSelection = getAbsenceSelectionById(
    firstAbsence.id,
    absences,
    true,
  );

  const isWithinGroupedSelection = groupedSelection
    ? slot.userId === groupedSelection.userId &&
      slot.start >= groupedSelection.start &&
      slot.end <= groupedSelection.end
    : false;

  const allSameType = selectedAbsences.every(
    (absence) =>
      firstAbsence.typeId != null &&
      absence.userId === firstAbsence.userId &&
      absence.typeId === firstAbsence.typeId &&
      absence.absenceTentative === firstAbsence.absenceTentative &&
      absence.approvedManager === firstAbsence.approvedManager &&
      absence.approvedHr === firstAbsence.approvedHr &&
      absence.allDay === firstAbsence.allDay,
  );

  const matchesTimes = selectedAbsences.every(
    (absence) =>
      absence.startTime === firstAbsence.startTime &&
      absence.endTime === firstAbsence.endTime,
  );

  const matchesSingleSelection =
    !!singleSelection &&
    slot.start === singleSelection.start &&
    slot.end === singleSelection.end &&
    slot.startTime === singleSelection.startTime &&
    slot.endTime === singleSelection.endTime;

  return (
    isWithinGroupedSelection &&
    allSameType &&
    (matchesTimes || matchesSingleSelection)
  );
}

export function isFullyOccupiedSlot(
  slot: TimelineSlot,
  absences: Absence[],
): slot is TimelineSlot & { start: string; end: string; userId: number } {
  if (!slot.start || !slot.end || !slot.userId || !slot.selectedIds.length) {
    return false;
  }

  const userAbsences = getUserAbsences(slot.userId, absences);
  if (getSlotFreeDates(slot, userAbsences).length > 0) {
    return false;
  }

  const isSingleDay = slot.start === slot.end;
  if (isSingleDay) {
    return true;
  }

  const selectedAbsences = slot.selectedIds
    .map((id) => absences.find((a) => a.id === id))
    .filter((a): a is Absence => !!a);
  const first = selectedAbsences[0];

  return (
    !!first &&
    selectedAbsences.every(
      (a) =>
        a.typeId === first.typeId &&
        a.userId === first.userId &&
        a.absenceTentative === first.absenceTentative &&
        a.approvedManager === first.approvedManager &&
        a.approvedHr === first.approvedHr &&
        a.allDay === first.allDay,
    )
  );
}
